import { useMemo, useState, type ReactNode } from 'react';
import {
  Callout,
  Card,
  Select,
  StatTile,
  cmpDecimal,
  formatDate,
  formatMoney,
  humanizeStatus,
} from '@biovet/ui';
import { useResource, type ReconLine, type ReconciliationView } from '../lib/api.js';
import type { RecordSet } from '../lib/design-data.js';
import { RecordGrid } from './Records.js';


const R = 'right' as const;

const FILTERS: Array<{
  key: string;
  label: (c: Counts) => string;
  test: (l: ReconLine) => boolean;
}> = [
  {
    key: 'open',
    label: (c) => `Unmatched (${c.unmatched + c.suggested})`,
    test: (l) => l.status === 'UNMATCHED' || l.status === 'SUGGESTED',
  },
  {
    key: 'matched',
    label: (c) => `Auto-matched (${c.matched})`,
    test: (l) => l.status === 'MATCHED',
  },
  {
    key: 'ignored',
    label: (c) => `Ignored (${c.ignored})`,
    test: (l) => l.status === 'IGNORED',
  },
  { key: 'all', label: (c) => `All (${c.total})`, test: () => true },
];

type Counts = ReconciliationView['counts'];

const NO_COUNTS: Counts = {
  total: 0,
  matched: 0,
  suggested: 0,
  unmatched: 0,
  ignored: 0,
  open: 0,
};

export function BankReconciliationScreen() {
  const [account, setAccount] = useState<string | undefined>(undefined);
  const res = useResource<ReconciliationView>('/bank-reconciliation', { account });

  const view = res.data;
  const counts = view?.counts ?? NO_COUNTS;
  const [chip, setChip] = useState(0);

  const lines = useMemo(
    () => (view?.lines ?? []).filter(FILTERS[chip]?.test ?? (() => true)),
    [view, chip],
  );

  const statement = view?.statement ?? null;
  const bank = view?.account ?? null;

  const set: RecordSet = {
    crumb: 'Finance & Accounts / Bank Reconciliation',
    title: bank ? `Bank reconciliation — ${bank.label}` : 'Bank reconciliation',
    subtitle: subtitleFor(view, res.loading),
    chips: FILTERS.map((f) => f.label(counts)),
    cols: [
      { label: 'Stmt date', width: '96px', mono: true },
      { label: 'Statement narration' },
      { label: 'Reference', width: '140px', mono: true },
      { label: 'Amount', align: R, mono: true, width: '150px' },
      { label: 'Matched voucher', width: '150px', mono: true },
      { label: 'Status', align: R, width: '130px', badge: true },
    ],
    rows: lines.map((l) => [
      formatDate(l.txnDate),
      l.description,
      l.reference ?? l.instrumentNo ?? '—',
      signedAmount(l),
      l.matchedVoucherNo ?? '—',
      humanizeStatus(l.status),
    ]),
    total: statement
      ? [
          '',
          `Per statement ${formatMoney(view!.totals.statementBalance, { decimals: 0 })} · per books ${formatMoney(
            view!.totals.ledgerBalance,
            { decimals: 0 },
          )} · difference`,
          '',
          formatMoney(view!.totals.difference, { decimals: 0 }),
          '',
          '',
        ]
      : undefined,
  };

  return (
    <RecordGrid
      set={set}
      loading={res.loading}
      error={res.error}
      chip={chip}
      onChipChange={setChip}
      emptyTitle={
        statement ? 'No statement lines in this view' : 'No bank statement has been imported yet'
      }
      actions={
        (view?.accounts.length ?? 0) > 1 ? (
          <Select
            aria-label="Bank account"
            value={bank?.code ?? ''}
            onChange={(e) => {
              setAccount(e.target.value || undefined);
              setChip(0);
            }}
          >
            {view?.accounts.map((a) => (
              <option key={a.code} value={a.code}>
                {a.label} · {a.currency}
              </option>
            ))}
          </Select>
        ) : undefined
      }
    >
      {!res.loading && !res.error && !statement && <NothingImported bank={bank?.label} />}

      {statement && (
        <>
          <Tiles>
            <Card>
              <StatTile
                label="Per statement"
                value={formatMoney(view!.totals.statementBalance, { decimals: 0 })}
                hint={`closing ${formatDate(statement.toDate)}`}
              />
            </Card>
            <Card>
              <StatTile
                label="Per books"
                value={formatMoney(view!.totals.ledgerBalance, { decimals: 0 })}
                hint="posted vouchers only"
              />
            </Card>
            <Card>
              <StatTile
                label="Difference"
                value={formatMoney(view!.totals.difference, { decimals: 0 })}
                hint={
                  cmpDecimal(view!.totals.difference, '0') === 0
                    ? 'reconciled'
                    : 'explained by the open items'
                }
              />
            </Card>
            <Card>
              <StatTile
                label="Open items"
                value={String(counts.open)}
                hint={`${counts.matched} matched of ${counts.total}`}
              />
            </Card>
          </Tiles>

          {view!.lastReconciled && (
            <div className="pb-3.5" data-noprint="1">
              <Callout tone="info" title="Last completed reconciliation">
                {formatDate(view!.lastReconciled.date)} at{' '}
                {formatMoney(view!.lastReconciled.balance, { decimals: 0 })} —{' '}
                {humanizeStatus(view!.lastReconciled.status)}.
              </Callout>
            </div>
          )}
        </>
      )}
    </RecordGrid>
  );
}

function NothingImported({ bank }: { bank?: string }) {
  return (
    <div className="pb-3.5" data-noprint="1">
      <Callout tone="warn" title="No statement imported">
        {bank ? `${bank} has ` : 'This account has '}no bank statement on file, so there is nothing
        to reconcile against. Import one — CSV or MT940 — and every figure on this screen is
        computed from it. Nothing here is sample data.
      </Callout>
    </div>
  );
}

function subtitleFor(view: ReconciliationView | undefined, loading: boolean): string {
  if (loading) return 'Reading the statement…';
  const s = view?.statement;
  if (!s) return 'Statement import (CSV / MT940), auto-match, unreconciled report.';
  const c = view!.counts;
  const parts = [
    `Statement ${s.statementNo ?? '—'} · ${formatDate(s.fromDate)} to ${formatDate(s.toDate)}`,
    `imported ${formatDate(s.importedAt.slice(0, 10))} from ${s.sourceFormat}`,
    `${c.total} line${c.total === 1 ? '' : 's'} · ${c.matched} matched · ${c.open} open`,
  ];
  return parts.join(' · ');
}

function signedAmount(l: ReconLine): string {
  const isDebit = cmpDecimal(l.debit, '0') > 0;
  const magnitude = formatMoney(isDebit ? l.debit : l.credit, { decimals: 0 });
  return isDebit ? `(${magnitude})` : magnitude;
}

function Tiles({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-2 gap-3.5 pb-3.5 lg:grid-cols-4">{children}</div>;
}
